import { useEffect } from 'react';

import ErrorHandler from '../components/ErrorHandler';
import { logout, setAuthData } from '../helpers/AuthHelpers';
import { dayjs } from '../helpers/DateTime';
import { useRefreshAccessTokenMutation } from '../services/rtk/AuthAPI';
import { AuthData } from '../types/Api';
import { User } from '../types/User';

const MOUSE_MOVE_KEY = 'lastMouseMoveTimestamp';
const INTERVAL_FREQUENCY_MS = 5000; // Frequency in milliseconds
const ALLOWED_INACTIVITY_MS = 120 * 60 * 1000; // minutes * seconds * milliseconds = 2 hours
const INTERVAL_ID_KEY = 'refreshIntervalKey';
const REFRESH_WINDOW_S = 5 * 60; // Minutes * seconds = 5 minutes

const mouseMoveEventHandler = () => {
    localStorage.setItem(MOUSE_MOVE_KEY, Date.now().toString());
};

export const clearUserIsActiveWatcher = (): void => {
    const intervalKey = localStorage.getItem(INTERVAL_ID_KEY);
    if (intervalKey) {
        clearInterval(parseInt(intervalKey, 10));
    }
    removeEventListener('mousemove', mouseMoveEventHandler);
    localStorage.removeItem(INTERVAL_ID_KEY);
};

export const tokenNeedsToBeRefreshed = (authData: AuthData): boolean => {
    return authData.expiresAt ? authData.expiresAt - REFRESH_WINDOW_S < dayjs().unix() : false;
};

export const useUserIsActiveWatcher = (currentUser: User | undefined): void => {
    const [refreshTokenMutation, refreshTokenResponse] = useRefreshAccessTokenMutation();

    useEffect(() => {
        if (currentUser) {
            // Clear an old one if it exists
            clearUserIsActiveWatcher();

            // Register a mouse event so users don't get kicked out after 5 seconds if they don't move the mouse, see ticket AM-572
            mouseMoveEventHandler();
            addEventListener('mousemove', mouseMoveEventHandler);

            const intervalId = setInterval(() => {
                const lastActivity = localStorage.getItem(MOUSE_MOVE_KEY);
                const authData = JSON.parse(localStorage.getItem('authData') as string);

                // If the user has not been active in ALLOWED_INACTIVITY_MS he/she will be logged out
                if (lastActivity === null || parseInt(lastActivity, 10) < Date.now() - ALLOWED_INACTIVITY_MS) {
                    logout();
                } else if (tokenNeedsToBeRefreshed(authData)) {
                    refreshTokenMutation(authData.refreshToken);
                }
            }, INTERVAL_FREQUENCY_MS);

            localStorage.setItem(INTERVAL_ID_KEY, intervalId.toString());
        }
    }, [currentUser]);

    useEffect(() => {
        if (refreshTokenResponse?.isSuccess) {
            setAuthData(refreshTokenResponse.data);
        } else if (refreshTokenResponse?.isError) {
            ErrorHandler(refreshTokenResponse, 'Failed to keep the user signed in');
            setTimeout(() => logout(), 10000);
        }
    }, [refreshTokenResponse]);
};
